/**
 * File Validation Utilities.
 *
 * Provides validation functions for vendor file uploads including: - File size validation - File
 * format validation - ZIP requirement validation.
 *
 * Validates Requirements: 1.5, 3.5, 3.6, 5.7.
 */

export interface FileValidationError {
  type: "FILE_TOO_LARGE" | "INVALID_FORMAT" | "ZIP_REQUIRED" | "INVALID_FILE"
  message: string
  suggestedFix: string
}

/**
 * Validates file size against maximum allowed size.
 *
 * @param file - File to validate.
 * @param maxSizeMB - Maximum allowed size in megabytes.
 * @returns Validation error if file is too large, null otherwise
 */
function validateFileSize(file: File, maxSizeMB: number): FileValidationError | null {
  const maxSizeBytes = maxSizeMB * 1024 * 1024

  if (file.size > maxSizeBytes) {
    return {
      type: "FILE_TOO_LARGE",
      message: `File size ${formatBytes(file.size)} exceeds maximum of ${maxSizeMB}MB`,
      suggestedFix: "Split the file into smaller chunks or compress the data",
    }
  }

  return null
}

/**
 * Validates file format against allowed formats.
 *
 * @param file - File to validate.
 * @param allowedFormats - Array of allowed file extensions (e.g., ['.csv', '.json', '.zip']). Empty
 *   array means accept all.
 * @returns Validation error if format is not allowed, null otherwise
 */
function validateFileFormat(file: File, allowedFormats: string[]): FileValidationError | null {
  // Empty array means accept all formats
  if (allowedFormats.length === 0) {
    return null
  }

  if (!file.name) {
    return {
      type: "INVALID_FILE",
      message: "File name is missing",
      suggestedFix: "Please select a valid file",
    }
  }

  const fileExt = getFileExtension(file.name)

  if (!fileExt) {
    return {
      type: "INVALID_FORMAT",
      message: "File has no extension",
      suggestedFix: `Add a file extension. Expected: ${allowedFormats.join(", ")}`,
    }
  }

  // Normalize extensions to lowercase for comparison
  const normalizedAllowedFormats = allowedFormats.map((fmt) => fmt.toLowerCase())
  const normalizedFileExt = fileExt.toLowerCase()

  if (normalizedAllowedFormats.indexOf(normalizedFileExt) === -1) {
    return {
      type: "INVALID_FORMAT",
      message: `File format ${fileExt} not allowed. Expected: ${allowedFormats.join(", ")}`,
      suggestedFix: `Convert file to one of: ${allowedFormats.join(", ")}`,
    }
  }

  return null
}

/**
 * Validates ZIP requirement for DTA.
 *
 * @param file - File to validate.
 * @param requireZip - Whether DTA requires ZIP format.
 * @returns Validation error if ZIP is required but file is not ZIP, null otherwise
 */
function validateZipRequirement(file: File, requireZip: boolean): FileValidationError | null {
  if (!requireZip) {
    return null
  }

  const fileExt = getFileExtension(file.name)

  if (fileExt?.toLowerCase() !== ".zip") {
    return {
      type: "ZIP_REQUIRED",
      message: "Files must be submitted as ZIP archives",
      suggestedFix: "Create a ZIP file containing your data files and upload it",
    }
  }

  return null
}

/**
 * Validates all file requirements and returns all errors.
 *
 * @param file - File to validate.
 * @param options - Validation options.
 * @returns Array of validation errors (empty if valid)
 */
export function validateFile(
  file: File,
  options: {
    maxSizeMB: number
    allowedFormats: string[]
    requireZip: boolean
  },
): FileValidationError[] {
  const errors: FileValidationError[] = []

  // Validate file size
  const sizeError = validateFileSize(file, options.maxSizeMB)
  if (sizeError) {
    errors.push(sizeError)
  }

  // Validate ZIP requirement first (takes precedence over format validation)
  const zipError = validateZipRequirement(file, options.requireZip)
  if (zipError) {
    errors.push(zipError)
  } else {
    // Only validate format if ZIP is not required or file is ZIP
    const formatError = validateFileFormat(file, options.allowedFormats)
    if (formatError) {
      errors.push(formatError)
    }
  }

  return errors
}

/**
 * Helper function to extract file extension from filename.
 *
 * @param filename - Name of the file.
 * @returns File extension including the dot (e.g., '.csv'), or null if no extension
 */
function getFileExtension(filename: string): string | null {
  const lastDotIndex = filename.lastIndexOf(".")

  if (lastDotIndex === -1 || lastDotIndex === filename.length - 1) {
    return null
  }

  return filename.substring(lastDotIndex)
}

/**
 * Helper function to format bytes into human-readable format.
 *
 * @param bytes - Number of bytes.
 * @param decimals - Number of decimal places (default: 2)
 * @returns Formatted string (e.g., '1.5 MB')
 */
export function formatBytes(bytes: number, decimals: number = 2): string {
  if (bytes === 0) return "0 Bytes"

  const k = 1024
  const dm = decimals < 0 ? 0 : decimals
  const sizes = ["Bytes", "KB", "MB", "GB", "TB"]

  const i = Math.floor(Math.log(bytes) / Math.log(k))

  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i]
}
